import { analyticsClient, experimentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { IS_MOCK_MODE } from '~/constants/config/env';
import type {
  ArchiveBatchRequest,
  ArchiveBatchResponse,
  ArchiveHistoryEntry,
  SetArchivedRequest,
} from '~/features/list-archive/types/list-archive.types';
import { runArchiveBatch } from '~/features/list-archive/utils/archive-batch';
import { daysAgoISO, diffInDays, nowISO } from '~/utils/date';

import { DateRange } from '../components/experiment-filters-constants';
import type {
  AnalyticsArmComparisonResponse,
  AnalyticsArmComparisonRow,
  AnalyticsDailyLiftResponse,
  AnalyticsMetricUnit,
  AnalyticsRoasComparisonResponse,
  AnalyticsRoasComparisonRow,
  AnalyticsRoasLiftDirection,
  AnalyticsRoasComparisonSection,
  CategoryPerformanceApiResponse,
  CategoryPerformanceApiRow,
} from '../types/analytics.types';
import type { ArmContentAttributes } from '../types/experiment-draft.types';
import type {
  AdjustedMetricsResponse,
  ArmComparisonResponse,
  ArmSlot,
  CategoryExclusionsResponse,
  CategoryPerformanceResponse,
  CategoryStandout,
  DailyLiftResponse,
  Experiment,
  ExperimentDetail,
  ExperimentKpis,
  ExperimentListParams,
  ExperimentPhase,
  HealthInStockTrends,
  PaginatedResponse,
  RoasComparisonResponse,
  CheckpointIcon,
  TimelineCheckpoint,
} from '../types/experiment.types';
import {
  ArmId,
  CheckpointStatus,
  ExperimentSplitMode,
  ExperimentStatus,
} from '../types/experiment.types';
import { armSlotForLetter, slotToArmId } from '../utils/arm-labels';
import { formatArmLift, resolveBestArm } from '../utils/arm-lift';
import { formatPercentFromDecimal } from '../utils/format-percent';
import { experimentPlatformFromAdPlatform } from '../utils/platform-mapping';

import {
  fetchAnalyticsArmComparison,
  fetchAnalyticsDailyLift,
  fetchAnalyticsExperimentSummary,
  fetchAnalyticsHealthAdjustedMetrics,
  fetchAnalyticsHealthCategoryExclusions,
  fetchAnalyticsHealthInStock,
  fetchAnalyticsRoasComparison,
  fetchAnalyticsTimeline,
  withSignal,
} from './analytics.service';

// ---------------------------------------------------------------------------
// Wire-protocol types
//
// Mirror the platform-gateway OpenAPI spec for `/workflow/v1/experiments`
// exactly; kept local to the service layer so the UI never touches API
// field names directly.
// ---------------------------------------------------------------------------

/** Spring Boot `ExperimentResponseDto` from `/workflow/v1/experiments[/{id}]`. */
interface ExperimentResponseDto {
  id: string;
  name: string;
  primaryKpi: string | null;
  secondaryKpi: string | null;
  numberOfArms: number | null;
  /** Control-first traffic-split percentages persisted alongside `numberOfArms`. Mirrors the draft DTO. */
  trafficSplit?: number[] | null;
  testDurationDays: number | null;
  contentAttributes: string[] | null;
  /** Per-arm selection keyed by enriched arm letter; `null` for single-selection runs. */
  armContentAttributes?: ArmContentAttributes | null;
  parentIdField: string | null;
  /** Step-2 scope filters — wire shape with multi-select arrays comma-joined. */
  scopingFilters?: Record<string, string> | null;
  /** Step-3 grouping filter — name + flat-string filter map. */
  groupingFilter?: {
    name: string | null;
    filters?: Record<string, string>;
    skuCount?: number;
  } | null;
  /** Total SKU count persisted on the Step-3 → Analytics-Setup PATCH; absent on rows that haven't reached that step yet. */
  skuCount?: number | null;
  /** Catalog snapshot id the draft was pinned to during step 2; absent on legacy rows that pre-date snapshot pinning. */
  syncId?: string | null;
  /** Ad-platform token persisted on create (e.g. `GOOGLE_ADS`, `META_ADS`); absent on legacy rows. */
  adPlatform?: string | null;
  stage: string | null;
  status: string;
  /** Post-creation journey step (e.g. CURRENT_CONTENT_SCORE, LIVE). `null` pre-trigger / legacy. */
  experimentPhase?: string | null;
  /** Latest scoring-failure message; `null` when no failure recorded. */
  scoringFailureReason?: string | null;
  /**
   * How the splitter built the Control / Test arms. `SCORED` is the baseline-matched
   * happy path; `RANDOM_FALLBACK` means analytics returned no rows and a round-robin
   * assignment was used. Null on legacy rows created before the column existed —
   * mapper treats null the same as `SCORED` (no banner).
   */
  splitMode?: string | null;
  /** Extra days appended to the test once results are inconclusive at the original end. */
  extendedDays?: number | null;
  createdAt: string;
  updatedAt: string;
  /** JWT-derived audit stamps (additive contract); actor is a string, absent on older builds. */
  createdBy?: string | null;
  lastActionBy?: string | null;
  lastActionAt?: string | null;
  /** List Archive visibility stamp — ISO instant when archived, `null`/absent
   *  while active. Independent of `status`. Optional: pre-rollout builds omit it. */
  archivedAt?: string | null;
  /** Who archived it (actor string, same shape as `createdBy`). Populated only
   *  while archived; `null` on rows archived before changeset 013. */
  archivedBy?: string | null;
  /** Visibility history, newest-first, max 50. Detail payloads only — `null`
   *  on list rows by design (changeset 014). */
  archiveHistory?: ArchiveHistoryEntry[] | null;
}

/** Spring paged envelope — only the fields we consume are declared here. */
interface PagedListExperimentResponseDto {
  content: ExperimentResponseDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

/** Normalize any API status string to our enum, falling back to DRAFT. */
function normalizeStatus(raw: string): ExperimentStatus {
  const upper = raw.toUpperCase();
  const allowed = Object.values(ExperimentStatus) as string[];
  return allowed.includes(upper) ? (upper as ExperimentStatus) : ExperimentStatus.DRAFT;
}

/**
 * Map the API's `splitMode` enum string to the UI type. Unknown / missing values
 * resolve to `null` — the detail page treats null the same as `SCORED`, so legacy
 * rows (or a future enum value that pre-dates this build) don't trip the warning
 * banner.
 */
function normalizeSplitMode(raw: string | null | undefined): ExperimentSplitMode | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  const allowed = Object.values(ExperimentSplitMode) as string[];
  return allowed.includes(upper) ? (upper as ExperimentSplitMode) : null;
}

/**
 * Days elapsed since an experiment was created. Returns 0 for pre-launch
 * states so the table shows "0 days" instead of a negative / inflated value
 * for drafts sitting in the backlog.
 */
function deriveProgressDays(createdAt: string, status: ExperimentStatus): number {
  if (status === ExperimentStatus.DRAFT || status === ExperimentStatus.CREATED) return 0;
  return Math.max(0, diffInDays(createdAt, nowISO()));
}

/**
 * Map the wire DTO to the UI's `Experiment` shape. Fields the API does not
 * currently expose (`metrics`, `alertCount`) are defaulted so the UI can
 * render a dash instead of a fabricated zero. `platform` is derived from
 * `adPlatform` (the same token sent on create-draft); `skuCount` is read
 * directly but normalized to `null` when the DTO value is missing or
 * non-positive so the table renders a dash instead of "0 SKUs". See
 * docs/experiments-module.md for the remaining gap list.
 */
function mapDtoToExperiment(dto: ExperimentResponseDto): Experiment {
  const status = normalizeStatus(dto.status);
  return {
    id: dto.id,
    name: dto.name,
    skuCount: typeof dto.skuCount === 'number' && dto.skuCount > 0 ? dto.skuCount : null,
    armCount: dto.numberOfArms ?? 0,
    platform: experimentPlatformFromAdPlatform(dto.adPlatform),
    status,
    // Wire value is forwarded as-is — the `ExperimentPhase` union has a
    // `(string & {})` fallback so a backend rollout that adds a value
    // won't break the mapper before the FE adopts it.
    experimentPhase: (dto.experimentPhase as ExperimentPhase | null | undefined) ?? null,
    scoringFailureReason: dto.scoringFailureReason ?? null,
    splitMode: normalizeSplitMode(dto.splitMode),
    extendedDays: typeof dto.extendedDays === 'number' ? dto.extendedDays : null,
    progressDays: deriveProgressDays(dto.createdAt, status),
    metrics: { ctrLift: null, cvrLift: null, revenueLift: null, roasLift: null },
    alertCount: 0,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    createdBy: dto.createdBy ?? null,
    lastActionBy: dto.lastActionBy ?? null,
    lastActionAt: dto.lastActionAt ?? null,
    archivedAt: dto.archivedAt ?? null,
    archivedBy: dto.archivedBy ?? null,
  };
}

// ---------------------------------------------------------------------------
// Real API calls
// ---------------------------------------------------------------------------

/**
 * Days each bounded date preset looks back; ALL_TIME is intentionally absent
 * (no lower bound). Translated to the list API's `createdAfter` at fetch time
 * (below) — not at the call site — so the TanStack Query key stays the stable
 * preset rather than a per-render timestamp that would thrash the cache.
 */
const DATE_RANGE_LOOKBACK_DAYS: Partial<Record<DateRange, number>> = {
  [DateRange.LAST_7_DAYS]: 7,
  [DateRange.LAST_30_DAYS]: 30,
  [DateRange.LAST_90_DAYS]: 90,
};

/**
 * Page of experiments from `/workflow/v1/experiments`. Converts one-indexed
 * UI pagination to Spring's zero-indexed `page` param and reshapes the
 * `PagedList<ExperimentResponseDto>` envelope into our `PaginatedResponse<T>`.
 *
 * Server-side filters forwarded to the list endpoint:
 *  - `status` — the selected statuses are sent as a CSV `status` param; the
 *    backend binds them to a List and filters `status IN (...)`.
 *  - `q` — free-text search from `params.search`; the backend does a
 *    case-insensitive substring match over the experiment name + id. The search
 *    box commits on Enter (SearchInput `commitOnEnter`), so this fires per
 *    submit, not per keystroke.
 *  - `dateRange` → `createdAfter` (ISO lower bound on the experiment's
 *    `createdAt`); ALL_TIME / unknown presets send no bound.
 *
 * Platform filtering was removed (experiments are Google-only).
 * TODO(AGENT-115-followup): add `createdBefore` if a future UI needs it.
 */
export async function fetchExperiments(
  params: ExperimentListParams,
): Promise<PaginatedResponse<Experiment>> {
  const searchParams: Record<string, string | number> = {
    page: Math.max(0, params.page - 1),
    size: params.pageSize,
  };
  if (params.statuses?.length) {
    // CSV `status` param — the backend binds it to a List<ExperimentStatus> and
    // filters `status IN (...)`. A single selection just sends one value.
    searchParams.status = params.statuses.join(',');
  }
  // Free-text search → backend `q` (case-insensitive substring over name + id).
  if (params.search) {
    searchParams.q = params.search;
  }
  // dateRange preset → createdAt lower bound. `as DateRange`: dateRange is
  // wire-typed as a string; unknown values miss the map → no bound (ALL_TIME too).
  const lookbackDays =
    params.dateRange !== undefined
      ? DATE_RANGE_LOOKBACK_DAYS[params.dateRange as DateRange]
      : undefined;
  if (lookbackDays !== undefined) {
    searchParams.createdAfter = daysAgoISO(lookbackDays);
  }
  // List Archive filter — omitted for the default list (server hides archived
  // rows), `ARCHIVED` for the Archived view, `ALL` for existence probes.
  if (params.archived) {
    searchParams.archived = params.archived;
  }

  const page = await experimentsClient
    .get(endpoints.experiments.list(), { searchParams })
    .json<PagedListExperimentResponseDto>();

  return {
    data: page.content.map(mapDtoToExperiment),
    total: page.totalElements,
    page: page.number + 1,
    pageSize: page.size,
    totalPages: page.totalPages,
  };
}

/**
 * Single experiment by id from `/workflow/v1/experiments/{id}`. The API does
 * not return segment options, so the dropdown on the detail header receives
 * an empty list until that concept is backed server-side.
 *
 * Passes `scopingFilters` + `groupingFilter` through unchanged (wire shape —
 * arrays comma-joined). Downstream callers convert to arrays where needed
 * (see content-enrichment's `combineExperimentFiltersForOverview`).
 */
export async function fetchExperiment(id: string): Promise<ExperimentDetail> {
  const dto = await experimentsClient
    .get(endpoints.experiments.byId(id))
    .json<ExperimentResponseDto>();

  const experiment = mapDtoToExperiment(dto);
  const groupingFilter = dto.groupingFilter
    ? {
        name: dto.groupingFilter.name,
        filters: dto.groupingFilter.filters ?? {},
        ...(dto.groupingFilter.skuCount !== undefined
          ? { skuCount: dto.groupingFilter.skuCount }
          : {}),
      }
    : null;
  return {
    ...experiment,
    runningSinceDays: experiment.progressDays,
    segments: [],
    primaryKpi: dto.primaryKpi ?? null,
    secondaryKpi: dto.secondaryKpi ?? null,
    contentAttributes: dto.contentAttributes ?? null,
    armContentAttributes: dto.armContentAttributes ?? null,
    parentIdField: dto.parentIdField ?? null,
    trafficSplit: dto.trafficSplit ?? null,
    scopingFilters: dto.scopingFilters ?? null,
    groupingFilter,
    syncId: dto.syncId ?? null,
    archiveHistory: dto.archiveHistory ?? null,
  };
}

/**
 * List Archive toggle — `PATCH experiments/{id}/archived` `{ archived }`. Sets
 * or clears `archivedAt` only (no status change; exactly reversible) and
 * returns the full updated `ExperimentResponseDto`, mapped to the list-row
 * shape so callers can read the new stamp without a refetch. 409 while the
 * experiment is RUNNING or any sub-status is in flight; unarchive is never refused.
 */
export async function setExperimentArchived(id: string, archived: boolean): Promise<Experiment> {
  const body: SetArchivedRequest = { archived };
  const dto = await experimentsClient
    .patch(endpoints.experiments.setArchived(id), { json: body })
    .json<ExperimentResponseDto>();
  return mapDtoToExperiment(dto);
}

/** Bulk archive / unarchive — `POST experiments/archived:batch` `{ ids, archived }`,
 *  chunked at the server's 100-id cap and merged. Always 200 when well-formed;
 *  read `summary` and each `results[].outcome`, never the HTTP status. */
export async function setExperimentsArchivedBatch(
  ids: readonly string[],
  archived: boolean,
): Promise<ArchiveBatchResponse> {
  return runArchiveBatch(ids, archived, (request: ArchiveBatchRequest) =>
    experimentsClient
      .post(endpoints.experiments.setArchivedBatch(), { json: request })
      .json<ArchiveBatchResponse>(),
  );
}

// ---------------------------------------------------------------------------
// Mocked fetchers
//
// These endpoints do not exist on the workflow-service API yet. Each keeps
// its original mock payload so the UI continues to render while the backend
// work is in flight. Replace with real API calls when available.
//
// TODO(AGENT-115-followup): timeline, stats, KPIs, performance, arm/ROAS
// comparison, daily lift, health panels.
// ---------------------------------------------------------------------------

const MOCK_TIMELINE: TimelineCheckpoint[] = [
  {
    id: 'cp-1',
    dayLabel: 'Day 0',
    name: 'Experiment Launched',
    status: CheckpointStatus.PASSED,
    details: 'Variant headlines deployed to GMC supplemental feed.',
    icon: 'gear',
  },
  {
    id: 'cp-2',
    dayLabel: 'Day 1–2',
    name: 'Traffic Distribution Check',
    status: CheckpointStatus.IN_PROGRESS,
    details:
      'Confirms both arms are receiving comparable impression and click volume. Flags any imbalance before the experiment proceeds.',
    icon: 'gear',
  },
  {
    id: 'cp-3',
    dayLabel: 'Day 3',
    name: 'Full Ramp Confirmed',
    status: CheckpointStatus.PENDING,
    details:
      'Impressions at 94% of pre-test baseline. Ramp-up data (Day 0–3) excluded from DID analysis.',
    icon: 'gear',
  },
  {
    id: 'cp-4',
    dayLabel: 'Day 7',
    name: 'Early Signal Review',
    status: CheckpointStatus.PENDING,
    details: 'First look at CTR and ROAS directional trends.',
    icon: 'people',
  },
  {
    id: 'cp-5',
    dayLabel: 'Day 17',
    name: 'Mid-Run Health Check',
    status: CheckpointStatus.PENDING,
    details:
      'Validates that metrics are trending in the expected direction. Option to pause the experiment if a significant negative signal is detected.',
    icon: 'gear',
  },
  {
    id: 'cp-6',
    dayLabel: 'Day 28',
    name: 'Results Readout',
    status: CheckpointStatus.PENDING,
    details:
      'Full summary across both arms — CTR lift, ROAS delta, and revenue impact. Recommended action available.',
    icon: 'gear',
  },
  {
    id: 'cp-7',
    dayLabel: 'Day 28 +7',
    name: 'Roll Out or End the Experiment',
    status: CheckpointStatus.PENDING,
    details:
      'Team reviews the readout and selects the next step: roll out the winning variant or end the experiment.',
    icon: 'people',
  },
];

const MOCK_ARM_COMPARISON: ArmComparisonResponse = {
  winner: ArmId.TEST_B,
  armsPresent: [ArmId.CONTROL, ArmId.TEST_A, ArmId.TEST_B],
  // Lift strings follow the real formatter: percent lift for rate / currency /
  // count metrics, absolute x-delta for ROAS (see `utils/arm-lift.ts`).
  rows: [
    {
      metric: 'Click-Through Rate',
      control: '2.14%',
      testA: '2.44%',
      testB: '2.61%',
      testALift: '+14.0%',
      testBLift: '+22.0%',
      bestArm: 'testB',
    },
    {
      metric: 'Conversion Rate',
      control: '3.82%',
      testA: '4.08%',
      testB: '4.31%',
      testALift: '+6.8%',
      testBLift: '+12.8%',
      bestArm: 'testB',
    },
    {
      metric: 'Revenue Per Product',
      control: '$42.10',
      testA: '$47.80',
      testB: '$51.40',
      testALift: '+13.5%',
      testBLift: '+22.1%',
      bestArm: 'testB',
    },
    {
      metric: 'ROAS',
      control: '4.2x',
      testA: '4.9x',
      testB: '5.3x',
      testALift: '+0.70x',
      testBLift: '+1.10x',
      bestArm: 'testB',
    },
    {
      metric: 'Impressions Per Product',
      control: '1.20M',
      testA: '1.22M',
      testB: '1.22M',
      testALift: '+1.7%',
      testBLift: '+1.7%',
      bestArm: 'testA',
    },
  ],
};

/** Mock mode fixture — a 3-arm run, so the second test column is exercised locally. */
const MOCK_ROAS_COMPARISON: RoasComparisonResponse = {
  controlArm: 'A',
  testArms: ['B', 'C'],
  sections: [
    {
      sourceLabel: 'Google Ads',
      rows: [
        {
          metric: 'Conversion Value',
          isRoas: false,
          control: '$256.75',
          testA: '$278.44',
          didLift: '+8.45%',
          didLiftDirection: 'positive',
          testB: '$241.10',
          testBDidLift: '-6.09%',
          testBDidLiftDirection: 'negative',
        },
        {
          metric: 'Ads Cost',
          isRoas: false,
          control: '$63.50',
          testA: '$59.05',
          didLift: '-7.01%',
          didLiftDirection: 'positive',
          testB: '$61.20',
          testBDidLift: '-3.62%',
          testBDidLiftDirection: 'positive',
        },
        {
          metric: 'ROAS (Conv. Value/Cost)',
          isRoas: true,
          control: '4.04x',
          testA: '4.72x',
          didLift: '+16.83%',
          didLiftDirection: 'positive',
          testB: '3.94x',
          testBDidLift: '-2.48%',
          testBDidLiftDirection: 'negative',
        },
      ],
    },
    {
      sourceLabel: 'GA4',
      rows: [
        {
          metric: 'Revenue',
          isRoas: false,
          control: '$345.47',
          testA: '$303.57',
          didLift: '-12.13%',
          didLiftDirection: 'negative',
          testB: '$351.02',
          testBDidLift: '+1.61%',
          testBDidLiftDirection: 'positive',
        },
        {
          metric: 'Ads Cost',
          isRoas: false,
          control: '$63.50',
          testA: '$59.04',
          didLift: '-7.02%',
          didLiftDirection: 'positive',
          testB: '$61.20',
          testBDidLift: '-3.62%',
          testBDidLiftDirection: 'positive',
        },
        {
          metric: 'ROAS (Revenue/Cost)',
          isRoas: true,
          control: '6.48x',
          testA: '6.66x',
          didLift: '+2.78%',
          didLiftDirection: 'positive',
          testB: '5.74x',
          testBDidLift: '-11.42%',
          testBDidLiftDirection: 'negative',
        },
      ],
    },
  ],
};

const MOCK_DAILY_LIFT_ROWS = [
  {
    day: 3,
    gmcImpr: 0.312,
    adsImpr: 0.018,
    gmcClicks: -0.019,
    adsClicks: -0.007,
    convValue: 0.0076,
    conversions: -0.0025,
    sessions: -0.0061,
    revenue: 0.0436,
  },
  {
    day: 4,
    gmcImpr: 0.295,
    adsImpr: 0.021,
    gmcClicks: 0.0082,
    adsClicks: -0.02,
    convValue: -0.003,
    conversions: 0.009,
    sessions: 0.039,
    revenue: -0.005,
  },
  {
    day: 5,
    gmcImpr: -0.019,
    adsImpr: 0.312,
    gmcClicks: 0.018,
    adsClicks: -0.007,
    convValue: 0.0076,
    conversions: -0.0025,
    sessions: -0.0061,
    revenue: 0.0436,
  },
  {
    day: 6,
    gmcImpr: -0.089,
    adsImpr: -0.023,
    gmcClicks: -0.0185,
    adsClicks: -0.009,
    convValue: -0.009,
    conversions: -0.003,
    sessions: -0.0045,
    revenue: -0.042,
  },
  {
    day: 7,
    gmcImpr: 0.021,
    adsImpr: 0.321,
    gmcClicks: 0.016,
    adsClicks: 0.003,
    convValue: 0.0075,
    conversions: 0.002,
    sessions: 0.0055,
    revenue: 0.045,
  },
  {
    day: 8,
    gmcImpr: 0.112,
    adsImpr: 0.029,
    gmcClicks: -0.012,
    adsClicks: -0.004,
    convValue: 0.011,
    conversions: 0.006,
    sessions: 0.018,
    revenue: 0.061,
  },
  {
    day: 9,
    gmcImpr: 0.151,
    adsImpr: 0.031,
    gmcClicks: -0.008,
    adsClicks: 0.001,
    convValue: 0.012,
    conversions: 0.004,
    sessions: 0.02,
    revenue: 0.07,
  },
  {
    day: 10,
    gmcImpr: 0.18,
    adsImpr: 0.032,
    gmcClicks: 0.019,
    adsClicks: 0.006,
    convValue: 0.014,
    conversions: 0.008,
    sessions: 0.022,
    revenue: 0.082,
  },
  {
    day: 11,
    gmcImpr: 0.188,
    adsImpr: 0.035,
    gmcClicks: 0.02,
    adsClicks: 0.007,
    convValue: 0.015,
    conversions: 0.009,
    sessions: 0.024,
    revenue: 0.085,
  },
  {
    day: 12,
    gmcImpr: 0.193,
    adsImpr: 0.037,
    gmcClicks: 0.022,
    adsClicks: 0.009,
    convValue: 0.016,
    conversions: 0.01,
    sessions: 0.027,
    revenue: 0.089,
  },
  {
    day: 13,
    gmcImpr: 0.2,
    adsImpr: 0.039,
    gmcClicks: 0.023,
    adsClicks: 0.01,
    convValue: 0.017,
    conversions: 0.011,
    sessions: 0.029,
    revenue: 0.091,
  },
  {
    day: 14,
    gmcImpr: 0.205,
    adsImpr: 0.041,
    gmcClicks: 0.024,
    adsClicks: 0.011,
    convValue: 0.018,
    conversions: 0.012,
    sessions: 0.031,
    revenue: 0.094,
  },
  {
    day: 15,
    gmcImpr: 0.215,
    adsImpr: 0.043,
    gmcClicks: 0.025,
    adsClicks: 0.012,
    convValue: 0.019,
    conversions: 0.012,
    sessions: 0.033,
    revenue: 0.101,
  },
  {
    day: 16,
    gmcImpr: 0.225,
    adsImpr: 0.045,
    gmcClicks: 0.026,
    adsClicks: 0.013,
    convValue: 0.02,
    conversions: 0.013,
    sessions: 0.034,
    revenue: 0.108,
  },
  {
    day: 17,
    gmcImpr: 0.233,
    adsImpr: 0.046,
    gmcClicks: 0.027,
    adsClicks: 0.014,
    convValue: 0.021,
    conversions: 0.013,
    sessions: 0.035,
    revenue: 0.113,
  },
  {
    day: 18,
    gmcImpr: 0.241,
    adsImpr: 0.048,
    gmcClicks: 0.028,
    adsClicks: 0.015,
    convValue: 0.022,
    conversions: 0.014,
    sessions: 0.036,
    revenue: 0.118,
  },
];

const MOCK_DAILY_LIFT_TREND = [
  { day: 3, testA: 0.008, testB: 0.022, control: -0.035 },
  { day: 4, testA: 0.002, testB: 0.015, control: -0.028 },
  { day: 5, testA: 0.011, testB: 0.009, control: -0.022 },
  { day: 6, testA: -0.018, testB: 0.006, control: -0.018 },
  { day: 7, testA: -0.017, testB: 0.005, control: -0.012 },
  { day: 8, testA: -0.014, testB: 0.004, control: -0.008 },
  { day: 9, testA: -0.012, testB: 0.01, control: -0.004 },
  { day: 10, testA: -0.01, testB: 0.006, control: 0 },
  { day: 11, testA: 0.002, testB: 0.003, control: 0.004 },
  { day: 12, testA: -0.004, testB: 0.008, control: 0.008 },
  { day: 13, testA: -0.006, testB: 0.006, control: 0.012 },
  { day: 14, testA: -0.003, testB: 0.005, control: 0.016 },
  { day: 15, testA: -0.005, testB: 0.004, control: 0.02 },
  { day: 16, testA: -0.006, testB: 0.002, control: 0.024 },
  { day: 17, testA: -0.008, testB: -0.018, control: 0.028 },
  { day: 18, testA: -0.008, testB: -0.02, control: 0.032 },
];

const MOCK_DAILY_LIFT: DailyLiftResponse = {
  rows: MOCK_DAILY_LIFT_ROWS,
  trend: MOCK_DAILY_LIFT_TREND,
  armsPresent: ['testA', 'testB', 'control'],
};

const MOCK_IN_STOCK_TRENDS: HealthInStockTrends = {
  kpis: [
    { label: 'Control In-Stock Rate', value: '96.2%' },
    { label: 'Test In-Stock Rate', value: '94.8%' },
    { label: 'OOS Skew (Test − Control)', value: '−1.4%' },
    { label: 'Skew < 10% threshold', value: 'Healthy', tone: 'success' },
  ],
  series: [
    { day: 3, test: 0.02, control: 0.02 },
    { day: 5, test: 0.15, control: 0.12 },
    { day: 7, test: 0.32, control: 0.3 },
    { day: 10, test: 0.48, control: 0.42 },
    { day: 14, test: 0.66, control: 0.55 },
    { day: 18, test: 0.92, control: 0.72 },
  ],
};

const MOCK_CATEGORY_EXCLUSIONS: CategoryExclusionsResponse = {
  highlightedColumn: 'sessions',
  rows: [
    {
      label: 'Post-Period',
      values: {
        gmcImpressions: { control: '1.62M', test: '1.47M' },
        gmcClicks: { control: '1.62M', test: '1.47M' },
        sessions: { control: '1.62M', test: '1.47M' },
        conversionValue: { control: '1.62M', test: '1.47M' },
        revenue: { control: '1.62M', test: '1.47M' },
      },
    },
    {
      label: 'Pre-Period',
      values: {
        gmcImpressions: { control: '1.62M', test: '1.47M' },
        gmcClicks: { control: '1.62M', test: '1.47M' },
        sessions: { control: '1.62M', test: '1.47M' },
        conversionValue: { control: '1.62M', test: '1.47M' },
        revenue: { control: '1.62M', test: '1.47M' },
      },
    },
  ],
};

const MOCK_ADJUSTED_METRICS: AdjustedMetricsResponse = {
  rows: [
    {
      arm: 'Test',
      duration: 431,
      parentIds: 2131,
      childIds: 184_196,
      prePeriodRevenue: '$169,529',
      postPeriodRevenue: '$130,839',
    },
    {
      arm: 'Control',
      duration: 431,
      parentIds: 2116,
      childIds: 183_174,
      prePeriodRevenue: '$177,252',
      postPeriodRevenue: '$148,899',
    },
  ],
  callouts: [
    {
      title: 'Revenue Impact of Adjustment',
      body: 'Test revenue dropped $14,667 less than Control after adjusting for available products, suggesting OOS-adjusted lift is more favorable for Lily content.',
    },
    {
      title: 'Product Count Variance',
      body: 'Test: 431 parent / 2,131 child  vs  Control: 431 parent / 2,116 child. Child ID variance is 0.7% — within acceptable range.',
    },
  ],
};

const KNOWN_CHECKPOINT_STATUSES = new Set<string>(Object.values(CheckpointStatus));

// Unknown wire values would index the status/icon component maps to `undefined`
// and crash the render, so fall back to a neutral value instead.
function toCheckpointStatus(status: string): CheckpointStatus {
  return KNOWN_CHECKPOINT_STATUSES.has(status)
    ? (status as CheckpointStatus)
    : CheckpointStatus.PENDING;
}

function toCheckpointIcon(icon: string): CheckpointIcon {
  return icon === 'people' ? 'people' : 'gear';
}

/**
 * Timeline checkpoints (Day 0 → Day N+ext). Backed by the analytics service
 * (`GET /api/v1/analytics/experiments/{id}/timeline`). Status is computed
 * server-side from elapsed days; we map the snake_case wire (`day_label`) into
 * the camelCase `TimelineCheckpoint` the UI renders. MSW serves the scripted
 * fixture in tests.
 */
export async function fetchExperimentTimeline(
  id: string,
  signal?: AbortSignal,
): Promise<TimelineCheckpoint[]> {
  if (IS_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_TIMELINE;
  }
  const wire = await fetchAnalyticsTimeline(id, signal);
  return wire.map((c) => ({
    id: c.id,
    dayLabel: c.day_label,
    name: c.name,
    status: toCheckpointStatus(c.status),
    details: c.details,
    icon: toCheckpointIcon(c.icon),
    // Lift and total-revenue sources match on today's paths; prefer the total's.
    revenueSource: c.total_revenue_source ?? c.revenue_source ?? null,
  }));
}

/**
 * Aggregated KPI tiles for the Performance-tab header. Backed by the
 * analytics service (`/api/v1/analytics/experiments/{id}/summary`). Errors
 * propagate to the caller's `isError` state so the UI can surface "data
 * unavailable" — we intentionally do NOT fall back to fabricated numbers,
 * since real-looking impressions/revenue on a broken/unauthorized fetch
 * mislead operators. `totalDays` is left `null` when the analytics summary
 * has no `end_date` (open-ended runs), so the UI omits the "of N" suffix.
 */
export async function fetchExperimentKpis(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<ExperimentKpis> {
  const summary = await fetchAnalyticsExperimentSummary(id, segment, signal);
  const startMs = new Date(summary.start_date).getTime();
  const endMs = summary.end_date ? new Date(summary.end_date).getTime() : null;
  const totalDays = endMs !== null ? Math.max(1, Math.round((endMs - startMs) / 86_400_000)) : null;
  const rawElapsed = Math.round((Date.now() - startMs) / 86_400_000);
  const elapsed =
    totalDays !== null ? Math.max(0, Math.min(totalDays, rawElapsed)) : Math.max(0, rawElapsed);
  return {
    daysElapsed: elapsed,
    totalDays,
    totalImpressions: summary.total_impressions,
    totalClicks: summary.total_clicks,
    totalConversions: summary.total_conversions,
    totalRevenue: Math.round(summary.total_revenue),
    revenueSource: summary.total_revenue_source ?? summary.revenue_source ?? null,
  };
}

/**
 * Arm comparison table. MSW serves the mock fixture in tests; in real envs
 * we fetch from agentic-analytics and adapt the wire shape (numbers + metric
 * IDs) into the existing display shape (pre-formatted strings + display
 * names) so the consuming card stays unchanged.
 */
export async function fetchExperimentArmComparison(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<ArmComparisonResponse> {
  if (IS_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_ARM_COMPARISON;
  }
  const wire = await fetchAnalyticsArmComparison(id, undefined, segment, signal);
  return transformArmComparison(wire);
}

/**
 * ROAS comparison. Same mock/real split as arm-comparison. Renders whatever
 * sections the wire sends — today a single `google_ads`, since GA4 bronze
 * carries no ad cost and so has no ROAS of its own.
 */
export async function fetchExperimentRoasComparison(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<RoasComparisonResponse> {
  if (IS_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_ROAS_COMPARISON;
  }
  const wire = await fetchAnalyticsRoasComparison(id, segment, signal);
  return transformRoasComparison(wire);
}

/** Daily DID lift. Maps snake_case wire keys to the UI's camelCase shape. */
export async function fetchExperimentDailyLift(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<DailyLiftResponse> {
  if (IS_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_DAILY_LIFT;
  }
  const wire = await fetchAnalyticsDailyLift(id, segment, signal);
  return transformDailyLift(wire);
}

// ---------------------------------------------------------------------------
// Wire → UI transformers for the three comparison/lift endpoints
//
// The agentic-analytics API returns raw numbers + metric IDs; the existing
// UI types expect pre-formatted display strings + human metric labels. Keep
// the formatting concentrated here so card components stay agnostic and a
// future locale-aware rewrite has one obvious place to land.
// ---------------------------------------------------------------------------

const ARM_METRIC_LABELS: Record<string, string> = {
  ctr: 'Click-Through Rate',
  conv_rate: 'Conversion Rate',
  revenue_per_product: 'Revenue Per Product',
  roas: 'ROAS',
  impressions_per_product: 'Impressions Per Product',
};

const ROAS_METRIC_LABELS: Record<string, string> = {
  conv_value: 'Conversion Value',
  revenue: 'Revenue',
  ads_cost: 'Ads Cost',
  roas: 'ROAS (Conv. Value/Cost)',
};

/**
 * `roas` label for the analytics sections. Ad spend is Google Ads' in every
 * section — neither GA4 nor Adobe carries cost — so the two ROAS rows differ
 * only by numerator, and naming that numerator is what stops a reader wondering
 * why one table shows two different ROAS figures.
 */
const ANALYTICS_ROAS_LABEL = 'ROAS (Revenue/Cost)';

const EM_DASH = '—';

/** Format an absolute value for a metric row. `null` becomes the em-dash. */
function formatMetricValue(value: number | null | undefined, unit: AnalyticsMetricUnit): string {
  if (value == null) return EM_DASH;
  switch (unit) {
    case 'percent':
      // Backend emits ratios (0..1); display as percent with 2 decimals.
      return `${(value * 100).toFixed(2)}%`;
    case 'currency':
      return `$${value.toFixed(2)}`;
    case 'ratio':
      return `${value.toFixed(2)}x`;
    case 'count':
      // Use locale grouping for raw counts (e.g. 3,636 not 3636).
      return Math.round(value).toLocaleString();
    default:
      return String(value);
  }
}

/** Format a lift delta. Prepends `+` for positive values; uses em-dash for null. */
function formatLiftValue(value: number | null | undefined, unit: AnalyticsMetricUnit): string {
  if (value == null) return EM_DASH;
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);
  switch (unit) {
    case 'percent':
      return `${sign}${(abs * 100).toFixed(2)}%`;
    case 'currency':
      return `${sign}$${abs.toFixed(2)}`;
    case 'ratio':
      return `${sign}${abs.toFixed(2)}x`;
    case 'count':
      return `${sign}${Math.round(abs).toLocaleString()}`;
    default:
      return `${sign}${String(abs)}`;
  }
}

/**
 * Map a wire AnalyticsArmComparisonRow to the display ArmComparisonRow. Lift
 * deltas are derived from the raw values rather than the wire's `test_*_lift`
 * fractions — see `utils/arm-lift.ts` for why.
 */
function transformArmRow(row: AnalyticsArmComparisonRow) {
  return {
    metric: ARM_METRIC_LABELS[row.metric] ?? row.metric,
    unit: row.unit,
    control: formatMetricValue(row.control, row.unit),
    testA: formatMetricValue(row.test_a, row.unit),
    testB: formatMetricValue(row.test_b, row.unit),
    testALift: formatArmLift(row.control, row.test_a, row.unit),
    testBLift: formatArmLift(row.control, row.test_b, row.unit),
    bestArm: resolveBestArm(row),
    source:
      row.source_label && row.description
        ? {
            source_label: row.source_label,
            description: row.description,
            // Revenue-family rows run GA4 → Adobe → Google Ads; landing on Google Ads is the
            // fallback. CTR / impressions are Google Merchant Center by design, never a fallback.
            is_fallback: row.source === 'google_ads',
          }
        : null,
  };
}

function transformArmComparison(wire: AnalyticsArmComparisonResponse): ArmComparisonResponse {
  const hasTestB = wire.rows.some((r) => r.test_b != null);
  const armsPresent: ArmId[] = [ArmId.CONTROL, ArmId.TEST_A];
  if (hasTestB) armsPresent.push(ArmId.TEST_B);

  // The backend now decides the winner and sends it as a domain arm letter
  // ("A" / "B" / "C"). This replaced a client-side row-win tally that had to
  // guess from lift signs; an unrecognised or absent letter yields `null`, so
  // the UI shows no winner rather than defaulting to Control (AGENT-196).
  const winnerSlot = armSlotForLetter(wire.winner);

  return {
    winner: winnerSlot ? slotToArmId(winnerSlot) : null,
    armsPresent,
    rows: wire.rows.map(transformArmRow),
  };
}

/** Badge direction from a lift's sign; null/undefined/0 read as neutral. */
function liftDirection(value: number | null | undefined): AnalyticsRoasLiftDirection {
  if (value == null || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
}

/**
 * Map a wire ROAS row to the display row. `source` decides the `roas` label:
 * ad spend is Google Ads' in every section, so an analytics section's ROAS is
 * that source's revenue over Ads spend and must not be labelled "Conv. Value".
 * ROAS values use the `ratio` unit; everything else is currency. DID lift is
 * always a **fraction** regardless of
 * the metric's own unit (`0.17021` = +17.02%), so it formats as a percent —
 * the metric unit would render it as `+0.17x` or `+$0.17`, both wrong.
 * The column reads `contra_lift` / `test_b_contra_lift` — the true DID (post gap
 * minus pre gap). `did_lift` is only the post-period gap, so it ignored
 * pre-period differences. The API sends no direction for the contra fields, so
 * the badge colour follows the sign.
 */
function transformRoasRow(row: AnalyticsRoasComparisonRow, source: string) {
  const unit: AnalyticsMetricUnit = row.metric === 'roas' ? 'ratio' : 'currency';
  const isAdsSection = source === 'google_ads';
  return {
    metric:
      row.metric === 'roas' && !isAdsSection
        ? ANALYTICS_ROAS_LABEL
        : (ROAS_METRIC_LABELS[row.metric] ?? row.metric),
    isRoas: row.metric === 'roas',
    control: formatMetricValue(row.control, unit),
    testA: formatMetricValue(row.test_a, unit),
    didLift: formatLiftValue(row.contra_lift, 'percent'),
    didLiftDirection: liftDirection(row.contra_lift),
    testB: formatMetricValue(row.test_b, unit),
    testBDidLift: formatLiftValue(row.test_b_contra_lift, 'percent'),
    testBDidLiftDirection: liftDirection(row.test_b_contra_lift),
  };
}

/**
 * Whether a source carries anything worth rendering. A section whose every value
 * is null on every row — a platform the tenant hasn't connected, or one with no
 * spend in the window — would otherwise render as a block of em dashes under its
 * own heading, reading as a broken section rather than an absent one. Checked
 * here, on the raw wire numbers, rather than in the component, which only sees
 * the formatted em-dash strings.
 */
function roasSectionHasData(section: AnalyticsRoasComparisonSection): boolean {
  return section.rows.some((r) => r.control != null || r.test_a != null || r.test_b != null);
}

/**
 * Map the wire ROAS response, carrying the arm letters through so the table can
 * head its columns from the payload instead of the fixed slot→letter mapping.
 * `test_arms` defaults to empty, which renders the single-test-arm table — the
 * shape an API predating the per-arm split still returns. Sources with no data
 * are dropped; all sources empty yields no sections, and the card shows its
 * "data unavailable" state rather than an empty grid.
 */
function transformRoasComparison(wire: AnalyticsRoasComparisonResponse): RoasComparisonResponse {
  return {
    controlArm: wire.control_arm ?? null,
    testArms: wire.test_arms ?? [],
    sections: wire.sections.filter(roasSectionHasData).map((s) => ({
      sourceLabel: s.source_label,
      rows: s.rows.map((r) => transformRoasRow(r, s.source)),
    })),
  };
}

/** Map snake_case wire keys to the UI's camelCase DailyLiftRow shape. */
function transformDailyLift(wire: AnalyticsDailyLiftResponse): DailyLiftResponse {
  // Derive armsPresent from the wire-side nulls: an arm is "present" if any
  // trend point has a non-null value for it. CONTROL is always reported as
  // the baseline reference. This mirrors PerformanceResponse so the daily-lift
  // chart can filter SERIES_META the same way and avoid drawing a misleading
  // 0% line for an arm that never had impressions.
  // Loose `!= null` so a backend regression that *omits* the key (delivers
  // `undefined` instead of explicit null) is also treated as absent —
  // matching the type's `number | null` intent rather than the literal
  // runtime check (`undefined !== null` is `true` and would mis-flag the
  // arm as present).
  const hasArm = (key: 'test_a' | 'test_b'): boolean => wire.trend.some((p) => p[key] != null);
  const armsPresent: ArmSlot[] = [];
  if (hasArm('test_a')) armsPresent.push('testA');
  if (hasArm('test_b')) armsPresent.push('testB');
  armsPresent.push('control');
  return {
    rows: wire.rows.map((r) => ({
      day: r.day,
      gmcImpr: r.gmc_impressions,
      adsImpr: r.ads_impressions,
      gmcClicks: r.gmc_clicks,
      adsClicks: r.ads_clicks,
      convValue: r.conv_value,
      conversions: r.conversions,
      sessions: r.sessions,
      revenue: r.revenue,
    })),
    trend: wire.trend.map((p) => ({
      day: p.day,
      // UI type is `number` (not nullable). Coalesce missing arm to 0 so
      // the chart still plots a continuous line; the consumer drops the
      // series entirely via armsPresent for arms that were never present.
      testA: p.test_a ?? 0,
      testB: p.test_b ?? 0,
      control: p.control ?? 0,
    })),
    armsPresent,
    revenueSource: wire.revenue_source ?? null,
  };
}

/**
 * Health → In-Stock Trends. The analytics `/health/in-stock` endpoint returns
 * the daily serving-availability series + KPIs in the UI shape verbatim, so we
 * pass it through. MSW serves the mock fixture in tests / dev.
 */
export async function fetchExperimentInStockTrends(
  id: string,
  signal?: AbortSignal,
): Promise<HealthInStockTrends> {
  if (IS_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_IN_STOCK_TRENDS;
  }
  return fetchAnalyticsHealthInStock(id, signal);
}

/** Health → Category Exclusions. Pre/post-period metric volumes per arm. */
export async function fetchExperimentCategoryExclusions(
  id: string,
  signal?: AbortSignal,
): Promise<CategoryExclusionsResponse> {
  if (IS_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_CATEGORY_EXCLUSIONS;
  }
  return fetchAnalyticsHealthCategoryExclusions(id, signal);
}

/** Health → Adjusted Metrics. Per-arm pre/post revenue + product counts. */
export async function fetchExperimentAdjustedMetrics(
  id: string,
  signal?: AbortSignal,
): Promise<AdjustedMetricsResponse> {
  if (IS_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return MOCK_ADJUSTED_METRICS;
  }
  return fetchAnalyticsHealthAdjustedMetrics(id, signal);
}

/**
 * Category Performance — powers the right-side Category Performance table AND
 * the chatbot category narration. Hits the analytics `/category-performance`
 * endpoint and maps its minimal wire shape (per-category rows) into the richer
 * FE model. Fields the endpoint doesn't supply yet (overall KPIs, weekly/daily
 * time-series, downloads) are returned empty — consumers render empty states.
 * The `category` arg drives only the FE dropdown + query key; the endpoint
 * returns every category in one response.
 */
export async function fetchExperimentCategoryPerformance(
  id: string,
  category: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<CategoryPerformanceResponse> {
  const api = await analyticsClient
    .get(endpoints.analytics.experiments.categoryPerformance(id), {
      // Pins the Weekly DiD / Daily-lift detail sections to the selected
      // category; omitted → the endpoint uses the top-revenue category.
      searchParams: { ...(category ? { category } : {}), ...(segment ? { segment } : {}) },
      ...withSignal(signal),
    })
    .json<CategoryPerformanceApiResponse>();
  return mapCategoryPerformance(api);
}

/** Backend DiD lifts are decimal fractions; the FE table/KPI renderers expect
 *  percent. `null` (CONTROL had no volume for the metric) → NaN so the percent
 *  formatter renders "—" rather than a misleading 0%. */
function toPct(value: number | null): number {
  return value == null ? Number.NaN : value * 100;
}

/**
 * Maps the analytics `/category-performance` wire shape onto the FE model. The
 * backend serves only the per-category table; `availableCategories` +
 * `categoryStandouts` are derived from those rows (pure min/max selections, not
 * fabricated metrics), and the unsupported sections come back empty.
 */
function mapCategoryPerformance(api: CategoryPerformanceApiResponse): CategoryPerformanceResponse {
  const { rows } = api;
  return {
    availableCategories: rows.map((r) => ({ value: r.category, label: r.category })),
    revenueSource: api.revenue_source ?? null,
    categoryPerformanceRows: rows.map((r) => ({
      category: r.category,
      revContributionPct: r.revenue_contribution_pct * 100,
      didImpressionPct: toPct(r.did_impression_lift),
      didClicksPct: toPct(r.did_clicks_lift),
      didConversionsPct: toPct(r.did_conversions_lift),
      didConvValuePct: toPct(r.did_conv_value_lift),
    })),
    categoryStandouts: deriveCategoryStandouts(rows),
    // Experiment-level lift aggregate for the six header KPI cards (revenue-
    // weighted roll-up across categories — reconciles with the table). `toPct`
    // turns a null metric (CONTROL had no volume) into NaN → the card renders
    // "—". `session_lift` is always null today. The key is omitted entirely
    // when the endpoint sends no aggregate, so the cards keep their empty state
    // (exactOptionalPropertyTypes forbids an explicit `undefined`).
    ...(api.kpis
      ? {
          kpis: {
            revenueLift: toPct(api.kpis.revenue_lift),
            conversionValueLift: toPct(api.kpis.conv_value_lift),
            conversionLift: toPct(api.kpis.conversion_lift),
            impressionLift: toPct(api.kpis.impression_lift),
            clickLift: toPct(api.kpis.click_lift),
            sessionLift: toPct(api.kpis.session_lift),
          },
        }
      : {}),
    // Per-category detail sections — the endpoint serves these for the pinned
    // `category` (wire shapes match the UI types verbatim). Default [] keeps
    // the panels' own empty states on older builds / experiments with no data.
    weeklyDidRevenue: api.weeklyDidRevenue ?? [],
    testVsControlRevenue: api.testVsControlRevenue ?? [],
    dailyDidLift: api.dailyDidLift ?? [],
  };
}

/** Top-performer / biggest-decline / largest-by-revenue picks from the
 *  per-category rows. Top/decline need a revenue-lift signal; all three are
 *  selections of real rows. Returns [] for an empty experiment. */
function deriveCategoryStandouts(rows: CategoryPerformanceApiRow[]): CategoryStandout[] {
  const standouts: CategoryStandout[] = [];
  const withRevLift = rows.filter((r) => r.did_revenue_lift != null);
  if (withRevLift.length > 0) {
    const top = withRevLift.reduce((a, b) =>
      (b.did_revenue_lift ?? 0) > (a.did_revenue_lift ?? 0) ? b : a,
    );
    const worst = withRevLift.reduce((a, b) =>
      (b.did_revenue_lift ?? 0) < (a.did_revenue_lift ?? 0) ? b : a,
    );
    standouts.push({
      label: 'Top Performer',
      primary: top.category,
      subValue: formatPercentFromDecimal(top.did_revenue_lift ?? 0, { precision: 'chart-1dp' }),
      subValueTrend: 'up',
    });
    // A *distinct* row is needed for a "biggest decline". With one ranked
    // category (the backend's interim everything-under-`Uncategorized` state) or
    // an all-equal tie, `reduce` returns the same row for both top and worst, so
    // skip the decline card rather than list one category as top AND worst.
    if (worst !== top) {
      standouts.push({
        label: 'Biggest Decline',
        primary: worst.category,
        subValue: formatPercentFromDecimal(worst.did_revenue_lift ?? 0, { precision: 'chart-1dp' }),
        subValueTrend: 'down',
      });
    }
  }
  if (rows.length > 0) {
    const largest = rows.reduce((a, b) =>
      b.revenue_contribution_pct > a.revenue_contribution_pct ? b : a,
    );
    standouts.push({
      label: 'Largest by Revenue',
      primary: largest.category,
      subValue: `Revenue Share ${(largest.revenue_contribution_pct * 100).toFixed(1)}%`,
    });
  }
  return standouts;
}
